/**
 * Камера модератора в кадре.
 *
 * СЛОЙ И КАРТИНКА ЖИВУТ ПОРОЗНЬ, и это главное в устройстве. Слой
 * лежит в сцене: место, размер, чья камера, — его двигают
 * и масштабируют, как всякий другой, и он переживает
 * переподключение хозяина. Картинка идёт транзиентом по сокету
 * и не сохраняется нигде.
 *
 * Отсюда состояние, которого нет у других слоёв: ИСТОЧНИК БЫВАЕТ
 * ОФЛАЙН. Модератор закрыл вкладку, выключил камеру, уехал —
 * слой остался. В эфире такого слоя не видно вовсе: чёрный
 * прямоугольник посреди трансляции хуже, чем ничего. В пульте
 * он виден плашкой — иначе его нельзя было бы найти и подвинуть.
 * Так же устроен недоступный эмбед.
 *
 * ПОЧЕМУ ДЕКОДЕР ЗДЕСЬ, А НЕ В ОВЕРЛЕЕ. Камеру показывают обе
 * стороны: зрителю — эфир, соседнему модератору — пульт, где он
 * этот слой двигает. Две реализации декодирования разошлись бы
 * молча, и разницу увидел бы кто-то один.
 */
import { asRecord, oneOf, optionalString } from './props.js';
const TYPE = 'camera';
const FITS = ['cover', 'contain'];
/** Сколько знаков отведено имени источника. */
const MAX_SOURCE = 64;
export const cameraWidget = {
    type: TYPE,
    parse(raw) {
        const props = asRecord(TYPE, raw);
        return {
            source: optionalString(TYPE, props, 'source', MAX_SOURCE),
            fit: oneOf(TYPE, props, 'fit', FITS, 'cover'),
            label: optionalString(TYPE, props, 'label', 64),
        };
    },
    mount(props) {
        const el = document.createElement('div');
        el.dataset.widget = TYPE;
        const canvas = document.createElement('canvas');
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.display = 'block';
        el.append(canvas);
        apply(el, canvas, props);
        return {
            el,
            update(next) {
                apply(el, canvas, next);
            },
            destroy() {
                el.remove();
            },
        };
    },
};
function apply(el, canvas, props) {
    /* eslint-disable no-param-reassign --
       Правило бережёт неизменяемость СОСТОЯНИЯ, а здесь меняется DOM —
       ровно то, ради чего функция и существует. */
    el.dataset.cameraSource = props.source;
    el.dataset.cameraLabel = props.label;
    // Признак живого источника ставит НЕ виджет, а тот, кто рисует
    // кадры: сам виджет о них не знает вовсе. До первого кадра —
    // «нет», и пульт рисует по этому признаку плашку
    if (el.dataset.cameraLive === undefined)
        el.dataset.cameraLive = 'no';
    canvas.dataset.cameraSource = props.source;
    canvas.style.objectFit = props.fit;
    /* eslint-enable no-param-reassign */
}
/**
 * Стена камер: декодеры по источникам и отрисовка в их холсты.
 *
 * ДЕКОДЕР ОДИН НА ИСТОЧНИК, А НЕ НА СЛОЙ. Одну и ту же камеру
 * можно положить в кадр дважды — крупно и уголком, — и декодировать
 * её дважды значило бы платить вдвое за одну и ту же картинку.
 *
 * УЗЛЫ ИЩУТСЯ В DOM, а не хранятся ссылками. Узлы слоёв держит
 * хост виджетов и наружу их не отдаёт; тот же приём, что у отметки
 * о неоткрывшемся файле в пульте. Слой мог родиться, умереть
 * и родиться заново между двумя кадрами — ссылка на его холст
 * протухла бы молча.
 */
export class CameraWall {
    root;
    onError;
    decoders = new Map();
    constructor(root, onError) {
        this.root = root;
        this.onError = onError;
    }
    /**
     * Приводит набор декодеров к списку вещающих.
     *
     * Списком целиком, а не «этот включился»: получатель, потерявший
     * одно сообщение, иначе остался бы с неверным набором навсегда.
     */
    setSources(sources) {
        const alive = new Set(sources.map((s) => s.clientId));
        for (const [id, stream] of this.decoders) {
            if (!alive.has(id)) {
                stream.close();
                this.decoders.delete(id);
                this.mark(id, false);
            }
        }
        for (const source of sources) {
            const current = this.decoders.get(source.clientId);
            // Настройки могли поменяться на ходу: модератор переключил
            // камеру или разрешение. Старый декодер такой поток
            // не разберёт, и чинить это придётся заменой
            if (current !== undefined && current.sameAs(source.config))
                continue;
            current?.close();
            this.decoders.set(source.clientId, new Stream(source.clientId, source.config, this));
        }
    }
    /** Кадр пришёл: декодируем и рисуем. */
    frame(frame) {
        this.decoders.get(frame.source)?.push(frame);
    }
    /** Прекращает работу: закрывает декодеры и гасит признаки. */
    destroy() {
        for (const [id, stream] of this.decoders) {
            stream.close();
            this.mark(id, false);
        }
        this.decoders.clear();
    }
    /** Рисует кадр во все холсты этого источника. */
    draw(source, image, width, height) {
        const canvases = this.nodesOf(source).filter((node) => node instanceof HTMLCanvasElement);
        for (const canvas of canvases) {
            // Размер холста ставится по КАДРУ, а не по слою: слой
            // растягивает картинку стилями, а рисовать её надо
            // в собственном разрешении, иначе она мылится дважды
            if (canvas.width !== width || canvas.height !== height) {
                canvas.width = width;
                canvas.height = height;
            }
            canvas.getContext('2d')?.drawImage(image, 0, 0, width, height);
        }
        this.mark(source, true);
    }
    report(source, error) {
        this.onError?.(source, error);
    }
    /**
     * Отмечает, есть ли у источника живая картинка.
     *
     * ПОГАСШИЙ ИСТОЧНИК СТИРАЕТ СВОИ ХОЛСТЫ, и это не уборка, а суть.
     * Холст держит нарисованное, пока в него не нарисуют другое:
     * выключенная камера иначе оставила бы в кадре ПОСЛЕДНИЙ КАДР —
     * замерший, но неотличимый от живого. Зритель смотрел бы
     * на застывшее лицо, а модератор видел бы в пульте картинку
     * с камеры, которой уже нет.
     */
    mark(source, live) {
        for (const node of this.nodesOf(source)) {
            if (node instanceof HTMLCanvasElement) {
                if (!live)
                    clear(node);
                continue;
            }
            node.dataset.cameraLive = live ? 'yes' : 'no';
        }
    }
    /**
     * Узлы этого источника: и слои, и их холсты.
     *
     * ИМЯ СРАВНИВАЕТСЯ, А НЕ ПОДСТАВЛЯЕТСЯ В СЕЛЕКТОР. Оно приходит
     * из сети, и всякая подстановка чужой строки в запрос — это
     * запрос, который однажды окажется другим запросом. Экранирование
     * здесь было, и на кавычке в имени оно всё равно давало
     * исключение: разные разборщики селекторов понимают escape
     * по-разному. Сравнение значений не понимает его никак — и потому
     * не ошибается.
     */
    nodesOf(source) {
        return Array.from(this.root.querySelectorAll('[data-camera-source]')).filter((node) => node.dataset.cameraSource === source);
    }
}
/**
 * Один источник: декодер и его состояние.
 *
 * ЖДЁТ КЛЮЧЕВОГО КАДРА. Декодер, подключившийся посреди вещания,
 * не разберёт промежуточный кадр: тот ссылается на картинку,
 * которой этот декодер не видел. Скормить такое — не мутный кадр,
 * а исключение.
 */
class Stream {
    id;
    config;
    wall;
    decoder = null;
    ready = false;
    constructor(id, config, wall) {
        this.id = id;
        this.config = config;
        this.wall = wall;
        this.open();
    }
    sameAs(other) {
        return (this.config.codec === other.codec &&
            this.config.width === other.width &&
            this.config.height === other.height &&
            this.config.description === other.description);
    }
    push(frame) {
        if (this.decoder === null)
            return;
        if (!this.ready) {
            if (!frame.keyframe)
                return;
            this.ready = true;
        }
        try {
            this.decoder.decode(new EncodedVideoChunk({
                type: frame.keyframe ? 'key' : 'delta',
                // Время нужно декодеру только для порядка кадров: своих
                // часов у потока нет, а номер кадра монотонен по построению
                timestamp: frame.seq * 1000,
                data: frame.payload,
            }));
        }
        catch (error) {
            this.fail(error);
        }
    }
    close() {
        const decoder = this.decoder;
        this.decoder = null;
        if (decoder === null || decoder.state === 'closed')
            return;
        try {
            decoder.close();
        }
        catch {
            // Закрытие уже закрытого — обычное дело при обрыве:
            // жаловаться не на что
        }
    }
    open() {
        if (typeof VideoDecoder === 'undefined') {
            this.wall.report(this.id, new Error('браузер не умеет VideoDecoder'));
            return;
        }
        try {
            const decoder = new VideoDecoder({
                output: (frame) => {
                    try {
                        this.wall.draw(this.id, frame, frame.displayWidth, frame.displayHeight);
                    }
                    finally {
                        // Кадр закрывается ВСЕГДА: VideoFrame держит память
                        // вне кучи сборщика, и потерянный кадр — это утечка
                        // в браузере зрителя, растущая тридцать раз в секунду
                        frame.close();
                    }
                },
                error: (error) => this.fail(error),
            });
            decoder.configure({
                codec: this.config.codec,
                codedWidth: this.config.width,
                codedHeight: this.config.height,
                ...(this.config.description === ''
                    ? {}
                    : { description: base64Bytes(this.config.description) }),
            });
            this.decoder = decoder;
        }
        catch (error) {
            this.fail(error);
        }
    }
    fail(error) {
        this.wall.report(this.id, error);
        this.close();
    }
}
/** Стирает нарисованное: камера погасла, и кадра больше нет. */
function clear(canvas) {
    canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
}
/** Base64 в байты: описание декодера приходит строкой. */
function base64Bytes(value) {
    const raw = atob(value);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1)
        out[i] = raw.charCodeAt(i);
    return out;
}
//# sourceMappingURL=camera.js.map